import { ArrowRight, Check, Clock } from 'lucide-react';
import { motion } from 'motion/react';
import { Link, useNavigate } from 'react-router';
import { PageMotion } from 'src/components/layout/page-motion.tsx';
import { SECTION_REVEAL, STAGGER_CHILD, STAGGER_CONTAINER } from 'src/lib/motion.ts';

import { BackgroundPaths } from '../components/ui/background-paths.tsx';
import { Eyebrow } from '../components/ui/eyebrow.tsx';
import { FrameworkScrollytelling } from '../components/ui/framework-scrollytelling.tsx';
import { PrimaryButton } from '../components/ui/primary-button.tsx';

export function LandingPage() {
  const navigate = useNavigate();
  const onStart = () => navigate('/check');

  return (
    <PageMotion>
      <section className="sticky top-0 z-0 h-[100svh] w-full overflow-hidden bg-zeb-hero">
        <BackgroundPaths className="text-white" />
        <div className="hero-recede relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-between px-5 pb-12 pt-28 sm:pt-32 lg:pb-16 lg:pt-36">
          <h1
            className="leading-[1.02] tracking-tight text-white"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: 'clamp(2.5rem, 7vw, 6.5rem)',
              letterSpacing: '-0.02em'
            }}>
            Wie kundenzentriert ist Ihre Organisation{' '}
            <span className="inline-block bg-zeb-secondary px-[0.18em] py-[0.04em] text-white [clip-path:polygon(0%_13%,99%_1%,100%_89%,1%_100%)]">
              wirklich?
            </span>
          </h1>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <PrimaryButton variant="contrast" size="lg" onClick={onStart}>
              Check starten
              <ArrowRight className="ml-2 h-5 w-5" />
            </PrimaryButton>
            <div className="flex items-center gap-2 text-sm text-white">
              <Clock className="h-4 w-4" />
              5–8 Minuten · entlang des zeb-Frameworks
            </div>
          </div>
        </div>
      </section>

      {/* Content-Wrapper mit solidem Hintergrund, der den Hero verdeckt. */}
      <div className="relative z-10 bg-background">
        <main className="mx-auto max-w-6xl px-5 lg:px-10">
          {/* Introtext von Patrick Neumann – gleitet zuerst über den Hero. */}
          <section className="py-20 lg:py-28">
            <div className="mx-auto max-w-3xl text-center">
              <p
                className="text-title-sm lg:text-title leading-snug text-zeb-tertiary"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  letterSpacing: '-0.01em'
                }}>
                Kundenzentrierung ist die vollständige Ausrichtung auf den Kundennutzen – und
                bestimmt, wie Organisationen denken, entscheiden und handeln.
              </p>
              <p
                className="text-title-sm lg:text-title leading-snug text-zeb-tertiary mt-4"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  letterSpacing: '-0.01em'
                }}>
                Mit dem{' '}
                <Link to="/check" className="text-zeb-primary underline-offset-2 hover:underline">
                  Customer Centricity Check
                </Link>{' '}
                analysieren Sie systematisch, wo Ihre Organisation heute steht und wo konkrete
                Handlungsfelder bestehen.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3">
                <img
                  src="/contacts/Patrick%20Neumann.jpg"
                  alt="Patrick Neumann"
                  className="h-16 w-16 shrink-0 rounded-full object-cover"
                />
                <div>
                  <div className="text-note font-medium text-zeb-tertiary">Patrick Neumann</div>
                  <div className="text-caption text-zeb-gray-500">Senior Manager</div>
                </div>
              </div>
            </div>
          </section>

          <motion.section
            {...SECTION_REVEAL}
            className="border-t border-zeb-muted py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <h2
                className="text-3xl lg:text-4xl tracking-tight text-zeb-tertiary"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}>
                Unser Verständnis von Kundenzentrierung
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p
                className="text-body-lg leading-relaxed text-zeb-gray-700 lg:-mt-1.5"
                style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                Kundenzentrierung ist ein <b>strategischer Anspruch</b>, der sich im täglichen
                Entscheiden, Arbeiten und Zusammenarbeiten beweisen muss.
              </p>
              <motion.ul
                {...STAGGER_CONTAINER}
                className="my-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-body-sm text-zeb-gray-700">
                {[
                  'begeistern Kunden durch relevante und einfache Erlebnisse,',
                  'steigern Zufriedenheit, Bindung und Weiterempfehlungsraten,',
                  'erhöhen Wachstum, Ertrag und Effizienz und',
                  'schaffen nachhaltige Differenzierung im Wettbewerb.'
                ].map((b) => (
                  <motion.li key={b} {...STAGGER_CHILD} className="flex items-start gap-3">
                    <Check className="mt-1 h-4 w-4 text-zeb-primary shrink-0" />
                    <span>{b}</span>
                  </motion.li>
                ))}
              </motion.ul>
              <p
                className="text-body-lg leading-relaxed text-zeb-gray-700 lg:-mt-1.5"
                style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                Der Weg zu echter Kundenzentrierung ist für die meisten Häuser jedoch ein{' '}
                <b>tiefgreifender organisatorischer und kultureller Wandel</b>.
              </p>
            </div>
          </motion.section>

          <motion.section {...SECTION_REVEAL} className="border-t border-zeb-muted py-16 lg:py-20">
            <Eyebrow size="body">Das Framework</Eyebrow>
            <h2
              className="mt-3 text-3xl lg:text-4xl tracking-tight text-zeb-tertiary max-w-2xl"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
              Drei Ebenen, die zusammen wirken.
            </h2>
            <p
              className="mt-4 max-w-2xl text-body leading-relaxed text-zeb-gray-700"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
              Der <b>Customer Centricity Check</b> basiert auf dem{' '}
              <b>zeb.Kundenzentrierung-Framework</b>, bestehend aus drei zentralen Ebenen:
            </p>

            <FrameworkScrollytelling />
          </motion.section>

          <motion.section
            {...SECTION_REVEAL}
            className="relative mb-20 overflow-hidden rounded-lg bg-zeb-hero px-7 py-12 lg:px-12 lg:py-14 text-white shadow-zeb-lg">
            <BackgroundPaths className="text-white" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <h2
                  className="text-2xl lg:text-3xl tracking-tight"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    letterSpacing: '-0.02em'
                  }}>
                  Bereit, Ihre Kundenzentrierung systematisch zu prüfen?
                </h2>
                <p className="mt-3 text-white max-w-2xl">
                  Unser Customer Centricity Check führt Sie in 5–8 Minuten entlang der drei
                  Framework-Ebenen und liefert einen Reifegrad mit konkreten Handlungsfeldern
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <PrimaryButton variant="contrast" onClick={onStart}>
                  Check starten
                  <ArrowRight className="ml-2 h-4 w-4" />
                </PrimaryButton>
              </div>
            </div>
          </motion.section>
        </main>
      </div>
    </PageMotion>
  );
}
